import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Lock, Gamepad2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import EventImage from "@/components/EventImage";
import PastEventStamp, { isPastEvent } from "@/components/PastEventStamp";
import ClosedEventStamp from "@/components/ClosedEventStamp";
import ClosedEventDialog from "@/components/ClosedEventDialog";
import ParticipationDialog from "@/components/ParticipationDialog";
import { formatDate, formatDateTime } from "@/constants";

const displayDate = (iso) => {
  if (!iso) return "";
  if (iso.includes("T00:00:00")) return formatDate(iso);
  return formatDateTime(iso);
};

function decorKind(title) {
  const t = (title || "").toLowerCase();
  if (t.includes("halloween")) return "halloween";
  if (t.includes("octobre rose")) return "octobre-rose";
  if (t.includes("noël") || t.includes("noel")) return "noel";
  if (t.includes("chandeleur")) return "chandeleur";
  if (t.includes("tournoi") || t.includes("sport")) return "esport";
  return null;
}

function NoelSnow() {
  const bulbs = [
    { x: 10.4, y: 6.5, c: "#ef4444", d: "0s" },
    { x: 24.7, y: 10.4, c: "#f59e0b", d: "0.3s" },
    { x: 39.2, y: 11.9, c: "#22c55e", d: "0.6s" },
    { x: 54, y: 11, c: "#ec4899", d: "1.5s" },
    { x: 68.9, y: 8.5, c: "#3b82f6", d: "0.9s" },
    { x: 92.4, y: 6.7, c: "#eab308", d: "1.2s" },
    { x: 109.6, y: 7.8, c: "#22c55e", d: "0.45s" },
  ];
  const flakes = [
    { cx: 6, fy: 27, fd: "3.1s", fde: "0s", fx: "2px" },
    { cx: 16, fy: 25, fd: "2.7s", fde: "0.9s", fx: "-2px" },
    { cx: 26, fy: 28, fd: "3.6s", fde: "1.7s", fx: "1px" },
    { cx: 36, fy: 26, fd: "2.9s", fde: "0.4s", fx: "-1px" },
    { cx: 46, fy: 28, fd: "3.3s", fde: "2.2s", fx: "2px" },
    { cx: 56, fy: 25, fd: "2.8s", fde: "1.2s", fx: "-2px" },
    { cx: 66, fy: 27, fd: "3.7s", fde: "0.6s", fx: "1px" },
    { cx: 76, fy: 26, fd: "3s", fde: "1.9s", fx: "-1px" },
    { cx: 86, fy: 28, fd: "2.6s", fde: "0.2s", fx: "2px" },
    { cx: 96, fy: 26, fd: "3.4s", fde: "1.4s", fx: "-2px" },
    { cx: 106, fy: 27, fd: "2.9s", fde: "2.4s", fx: "1px" },
    { cx: 116, fy: 25, fd: "3.2s", fde: "1s", fx: "-1px" },
  ];
  return (
    <span className="inline-block mt-1" data-testid="decor-noel" aria-hidden="true">
      <svg viewBox="0 0 120 46" className="h-14 w-40 overflow-visible">
        <path d="M2 3 Q30 16 60 10 Q90 4 118 9" stroke="#78716c" strokeWidth="1" fill="none" />
        {bulbs.map((b, i) => (
          <g key={`b${i}`} className="decor-bulb" style={{ color: b.c, animationDelay: b.d }} data-testid={i === 0 ? "decor-noel-guirlande" : undefined}>
            <line x1={b.x} y1={b.y} x2={b.x} y2={b.y + 2} stroke="#78716c" strokeWidth="0.8" />
            <circle cx={b.x} cy={b.y + 3.3} r="2.3" fill={b.c} />
          </g>
        ))}
        <ellipse className="noel-ground-snow" cx="60" cy="42" rx="48" ry="4" fill="#e0f2fe" stroke="#bae6fd" strokeWidth="0.8" />
        <ellipse className="noel-ground-snow" cx="30" cy="40.5" rx="16" ry="2.6" fill="#f0f9ff" />
        <ellipse className="noel-ground-snow" cx="88" cy="41" rx="14" ry="2.4" fill="#f0f9ff" />
        {flakes.map((f, i) => (
          <circle key={i} className="noel-flake" cx={f.cx} cy="15" r="1.7" fill="#dbeafe"
            style={{ "--fy": `${f.fy}px`, "--fd": f.fd, "--fde": f.fde, "--fx": f.fx }} />
        ))}
      </svg>
    </span>
  );
}

function Chandeleur() {
  return (
    <span className="relative inline-block mt-1" data-testid="decor-chandeleur">
      <img src="/assets/decor-chandeleur.png" alt="Enfants faisant sauter des crêpes à tour de rôle"
        className="h-24 w-auto rounded-xl shadow-md border border-slate-100" />
      <span className="crepe crepe-1" />
      <span className="crepe crepe-2" />
    </span>
  );
}

function Ghost() {
  return (
    <svg viewBox="0 0 32 40" className="h-8 w-7 mb-0.5 decor-ghost" aria-hidden="true" data-testid="decor-halloween-ghost">
      <path d="M16 2C9 2 5 8 5 15v21l4-3 3.5 3 3.5-3 3.5 3 3.5-3 4 3V15c0-7-4-13-11-13z" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />
      <circle cx="11.5" cy="14" r="2" fill="#334155" />
      <circle cx="20.5" cy="14" r="2" fill="#334155" />
    </svg>
  );
}

function MusicNotes() {
  return (
    <span className="flex items-center gap-2.5 mt-1.5 text-orange-500" data-testid="decor-halloween-notes" aria-hidden="true">
      <svg viewBox="0 0 16 24" className="h-5 w-4 decor-note">
        <ellipse cx="5" cy="19" rx="4" ry="3" fill="currentColor" transform="rotate(-20 5 19)" />
        <path d="M8.5 18.5V5c2.5.5 4.5 2 5 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
      <span className="text-xl leading-none decor-note font-serif" style={{ animationDelay: "0.5s" }}>𝄞</span>
      <svg viewBox="0 0 16 24" className="h-4 w-3.5 decor-note" style={{ animationDelay: "1.1s" }}>
        <ellipse cx="5" cy="19" rx="4" ry="3" fill="currentColor" transform="rotate(-20 5 19)" />
        <path d="M8.5 18.5V5c2.5.5 4.5 2 5 4.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function Pumpkin() {
  return (
    <svg viewBox="0 0 48 48" className="h-10 w-10 mt-1" aria-hidden="true" data-testid="decor-halloween">
      <path d="M24 5c-2 0-3 2-3 4" stroke="#166534" strokeWidth="3" fill="none" strokeLinecap="round" />
      <ellipse cx="24" cy="27" rx="18" ry="14" fill="#f97316" />
      <ellipse cx="16" cy="27" rx="7" ry="13" fill="#ea580c" opacity="0.55" />
      <ellipse cx="32" cy="27" rx="7" ry="13" fill="#ea580c" opacity="0.55" />
      <path className="decor-eyes" d="M14 23l4-5 4 5z" />
      <path className="decor-eyes" d="M26 23l4-5 4 5z" />
      <path d="M17 33c3 3 11 3 14 0" stroke="#7c2d12" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function PinkRibbon() {
  return (
    <svg viewBox="0 0 40 48" className="h-10 w-8 mt-1 decor-pulse-pink" aria-hidden="true" data-testid="decor-octobre-rose">
      <path d="M20 3c-6.5 0-10 4.5-10 9 0 6.5 5.5 13 10 19 4.5-6 10-12.5 10-19 0-4.5-3.5-9-10-9z" fill="#f472b6" />
      <path d="M14 30l-6 15 8-5.5 4 7.5 4-7.5 8 5.5-6-15" fill="#ec4899" />
      <circle cx="20" cy="12" r="3.5" fill="#fbcfe8" />
    </svg>
  );
}

function EsportMini() {
  return (
    <span className="flex items-center gap-2 mt-1" data-testid="decor-esport">
      <Gamepad2 className="h-8 w-8 text-cyan-600 decor-glow-cyan" />
      <span className="inline-block h-6 w-9 rounded-md bg-slate-800 relative overflow-hidden shadow">
        <span className="absolute inset-0.5 rounded bg-gradient-to-br from-cyan-400 to-fuchsia-500 opacity-80 decor-glow-cyan" />
      </span>
    </span>
  );
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participateEvent, setParticipateEvent] = useState(null);
  const [closedEvent, setClosedEvent] = useState(null);

  useEffect(() => {
    if (user === undefined) return;
    const endpoint = user ? "/member/events" : "/events";
    api.get(endpoint)
      .then((r) => setEvents(r.data))
      .catch(() => api.get("/events").then((r) => setEvents(r.data)).catch(() => {}))
      .finally(() => setLoading(false));
  }, [user]);

  const upcoming = events.filter((e) => new Date(e.date) >= new Date(new Date().toDateString()));
  const past = events.filter((e) => !upcoming.includes(e));

  useEffect(() => {
    if (loading || !window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [loading, events]);

  const renderEvent = (e) => {
    const kind = decorKind(e.title);
    const pastEvent = isPastEvent(e.date);
    return (
    <ClosedEventStamp key={e.id} active={!pastEvent && e.registration_open === false}>
    <Card id={`event-${e.id}`} className="event-anchor scroll-mt-24 border-slate-200 hover:shadow-md transition-shadow" data-testid={`event-card-${e.id}`}>
      <CardContent className="p-6 flex gap-5">
        {e.image_url ? (
          <PastEventStamp active={pastEvent} className="shrink-0">
            <EventImage src={e.image_url} alt={`Affiche ${e.title}`}
              className="w-28 sm:w-36 aspect-[3/4] rounded-2xl object-cover shadow-md border border-slate-100"
              testId={`event-image-${e.id}`} />
            {e.members_only && !pastEvent && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" data-testid={`event-members-stamp-${e.id}`}>
                <div className="-rotate-6 rounded-lg border-4 border-amber-500/90 bg-amber-50/90 px-3 py-2 shadow-xl backdrop-blur-[1px]">
                  <p className="text-center text-[11px] sm:text-sm font-black uppercase tracking-wider text-amber-700 leading-tight">Réservé<br />aux adhérents</p>
                </div>
              </div>
            )}
          </PastEventStamp>
        ) : (
          <div className="shrink-0 w-16 text-center">
            <div className={`${pastEvent ? "bg-slate-400" : "bg-sky-700"} text-white rounded-t-xl px-2 py-1 text-xs font-semibold uppercase`}>
              {new Date(e.date).toLocaleDateString("fr-FR", { month: "short" })}
            </div>
            <div className={`${pastEvent ? "bg-slate-100 text-slate-500" : "bg-sky-50 text-sky-800"} rounded-b-xl px-2 py-2 font-heading text-2xl font-extrabold`}>
              {new Date(e.date).getDate()}
            </div>
            {pastEvent && (
              <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-red-600" data-testid={`event-past-label-${e.id}`}>Passé</p>
            )}
            {!pastEvent && e.registration_open === false && (
              <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500" data-testid={`event-closed-label-${e.id}`}>Complet</p>
            )}
          </div>
        )}
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-800">{e.title}</h3>
            {pastEvent && (
              <Badge className="bg-red-100 text-red-700 border border-red-200 hover:bg-red-100" data-testid={`event-past-badge-${e.id}`}>
                Événement passé
              </Badge>
            )}
            {e.members_only && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100" data-testid={`event-members-only-${e.id}`}>
                <Lock className="h-3 w-3 mr-1" />Réservé aux adhérents
              </Badge>
            )}
          </div>
          {pastEvent ? (
            <p className="text-sm flex items-center gap-1 text-slate-500" data-testid={`event-date-${e.id}`}>
              <CalendarDays className="h-3.5 w-3.5" />{displayDate(e.date)}
            </p>
          ) : (
            <button type="button" onClick={() => { if (e.registration_open === false) { setClosedEvent(e); } else { setParticipateEvent(e); } }}
              title="Cliquez pour enregistrer votre participation"
              className={`text-sm inline-flex items-center gap-1 cursor-pointer transition-all hover:scale-[1.04] ${
                kind === "halloween"
                  ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold px-3 py-1 rounded-full shadow-md -rotate-1"
                  : kind === "octobre-rose"
                  ? "bg-pink-100 text-pink-700 border-2 border-pink-400 font-semibold px-3 py-1 rounded-full decor-pulse-pink"
                  : "bg-gradient-to-b from-sky-50 to-sky-100 text-sky-800 font-semibold px-3 py-1.5 rounded-xl shadow-[0_3px_0_#bae6fd,0_6px_12px_rgba(2,132,199,0.18)] hover:shadow-[0_1px_0_#bae6fd,0_3px_8px_rgba(2,132,199,0.18)] hover:translate-y-[2px] active:translate-y-[3px] active:shadow-none"
              }`} data-testid={`event-date-${e.id}`}>
              <CalendarDays className="h-3.5 w-3.5" />{displayDate(e.date)}
            </button>
          )}
          {kind === "halloween" && (
            <span className="inline-block">
              <span className="flex items-end gap-3">
                <Pumpkin />
                <Ghost />
              </span>
              <MusicNotes />
            </span>
          )}
          {kind === "octobre-rose" && <PinkRibbon />}
          {kind === "noel" && <NoelSnow />}
          {kind === "chandeleur" && <Chandeleur />}
          {kind === "esport" && <EsportMini />}
          {e.location && (
            <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location}</p>
          )}
          <p className="text-sm leading-relaxed text-slate-600 whitespace-pre-line">{e.description}</p>
        </div>
      </CardContent>
    </Card>
    </ClosedEventStamp>
    );
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20" data-testid="events-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Agenda</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="events-title">Événements</h1>
          <p className="text-base text-slate-600 mt-3">
            Retrouvez ici toutes les manifestations organisées par l'association. Certains événements
            sont réservés aux adhérents connectés.
          </p>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500" data-testid="events-loading">Chargement…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid="events-empty">
            Aucun événement programmé pour le moment — revenez bientôt !
          </p>
        ) : (
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-800">À venir</h2>
              {upcoming.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="events-upcoming-empty">Aucun événement à venir.</p>
              ) : (
                upcoming.map(renderEvent)
              )}
            </div>
            {past.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-slate-800">Événements passés</h2>
                <p className="text-sm text-slate-500">Merci de votre mobilisation et merci à nos bénévoles pour ces moments partagés.</p>
                {past.map(renderEvent)}
              </div>
            )}
          </div>
        )}
      </div>
      <ParticipationDialog event={participateEvent} open={!!participateEvent}
        onOpenChange={(o) => { if (!o) setParticipateEvent(null); }} />
      <ClosedEventDialog event={closedEvent} open={!!closedEvent}
        onOpenChange={(o) => { if (!o) setClosedEvent(null); }} />
    </div>
  );
}
